/**
 * Learn app entry (SCH Milestone A — Schoology learner PWA).
 *
 * Same provider stack as the other SPAs: `PosTrpcProvider` →
 * `RouterProvider`. URL resolution: `VITE_API_URL` → dev
 * `http://localhost:4000/trpc` → prod same-origin `/trpc`.
 *
 * Learners are real authenticated users (Better-Auth session cookie,
 * learner-as-customer identity bridge per SCH-1) — auth gating lives in
 * `src/lib/auth.tsx` (`<RequireAuth>`), not here.
 */
import { StrictMode, type ReactElement } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import {
  PosTrpcProvider,
  createPosQueryClient,
  createPosReactTrpcClient,
  useWorkspaceSwitchedElsewhere,
} from '@mangrove/trpc-client/react';
import { WorkspaceSwitchedBanner, createOfflinePersister } from '@mangrove/ui';
import { WakingUpGate } from './components/waking-up-gate.js';
import { trpc } from './lib/trpc.js';
import { LEARN_CACHE_DB_NAME } from './lib/auth.js';
import { routeTree } from './routeTree.gen.js';
import './styles.css';

const queryClient = createPosQueryClient();

// ADR-0056 Layer 1: IndexedDB-backed cache persistence. The catalog +
// the learner's enrolled courses load instantly on revisit; mutations
// issued offline queue through a connectivity flap. The persister key
// is UNIQUE per app so learn never shares a cache with another surface
// on the same origin. The db name is the shared constant from
// `lib/auth.tsx` so the §3.3 shared-device wipe (`wipeClientCaches`)
// always purges the exact bucket this persister writes.
const persister = createOfflinePersister(LEARN_CACHE_DB_NAME);

const trpcUrl =
  (import.meta.env['VITE_API_URL'] as string | undefined) ??
  (import.meta.env.DEV ? 'http://localhost:4000/trpc' : '/trpc');

const trpcClient = createPosReactTrpcClient(trpc, { url: trpcUrl });

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('#root element missing from index.html');

/**
 * #803: after this tab reloaded because its workspace was switched in another
 * tab or app, say so once (the reload itself is PosTrpcProvider's).
 */
function WorkspaceSwitched(): ReactElement | null {
  const { shown, dismiss } = useWorkspaceSwitchedElsewhere();
  return shown ? <WorkspaceSwitchedBanner onDismiss={dismiss} /> : null;
}

createRoot(rootElement).render(
  <StrictMode>
    <PosTrpcProvider
      trpc={trpc}
      client={trpcClient}
      queryClient={queryClient}
      persister={persister}
    >
      <WorkspaceSwitched />
      <WakingUpGate>
        <RouterProvider router={router} />
      </WakingUpGate>
    </PosTrpcProvider>
  </StrictMode>,
);

// Register the service worker for PWA install-ability + Web Push. ADR-0055.
// Skipped in dev to keep HMR cache-free; install + push are install-time features.
if ('serviceWorker' in navigator && !import.meta.env.DEV) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => {
      console.warn('Service worker registration failed', err);
    });
  });
}
